/**
 * Mark — the WebNova brand mark.
 *
 * Renders the 3D möbius icon as a PNG served from `/brand/webnova-icon.png`.
 * The file is in `public/` so Vite ships it as-is (no hash) at a stable URL
 * the site, the favicon, and any future og:image can all reference.
 *
 * We moved off the inline stroked SVG because:
 *   1. The PNG is the real render with depth and light — the inline SVG was
 *      a flat approximation that didn't match the master lockup.
 *   2. The PNG has a transparent background and is small enough (~2 MB source
 *      but downsamples fine at 22–26px; Vite + host should serve responsive
 *      variants if this becomes a concern).
 *   3. Keeping the API stable (`size`, `title`) means Nav/Footer don't change.
 *
 * The `title` prop remains optional — when omitted the image is decorative
 * and aria-hidden. When passed, it becomes the alt text and the img loses
 * aria-hidden, matching how the SVG version handled a11y.
 */
interface MarkProps {
  /** Rendered pixel size. Defaults to 26, matching the nav. */
  size?: number;
  /** If provided, used as alt text; also removes aria-hidden. */
  title?: string;
}

export default function Mark({ size = 26, title }: MarkProps) {
  // 256px source covers hi-DPI at every nav/footer size we render today
  // (up to 64px physical = 128px @2x). 25 KB on the wire vs 2.2 MB for
  // the master render.
  return (
    <img
      src="/brand/webnova-icon-256.png"
      width={size}
      height={size}
      alt={title ?? ''}
      aria-hidden={title ? undefined : true}
      decoding="async"
      loading="eager"
      draggable={false}
      style={{ width: size, height: size, display: 'block', objectFit: 'contain' }}
    />
  );
}
